import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ArrowRight, Sparkles, Grid3X3, Star, Play, ChevronRight } from 'lucide-react';
import { products, categories } from '../data/products';
import { getThumbPath } from '../utils/thumbnails';

const comingSoonCats = new Set(categories.filter(c => c.comingSoon).map(c => c.id));
const heroProducts = products.filter(p => !comingSoonCats.has(p.category));

const tileThemes = [
  { pattern: /MIDNIGHT|MID NIGHT|NIGHT|BLACK|DARK|CHARCOAL|SLATE|ONYX/i, bg: 'from-slate-900 via-gray-900 to-slate-800', text: 'text-white', accent: 'text-amber-400' },
  { pattern: /WHITE|ARCTIC|PEARL|IVORY|CREAM|BIANCO|BLANCO|SNOW/i, bg: 'from-gray-100 via-white to-gray-200', text: 'text-gray-900', accent: 'text-amber-600' },
  { pattern: /BLUE|OCEAN|AQUA|AZURE|SAPPHIRE|COBALT|NAVY|MARINE|INFINITY|LAGOON/i, bg: 'from-blue-900 via-cyan-800 to-blue-800', text: 'text-white', accent: 'text-cyan-300' },
  { pattern: /GREEN|FOREST|EMERALD|OLIVE|SAGE|MINT|JADE/i, bg: 'from-emerald-900 via-green-800 to-emerald-800', text: 'text-white', accent: 'text-emerald-300' },
  { pattern: /RED|CORAL|ROSE|CRIMSON|RUBY|CHERRY|BURGUNDY/i, bg: 'from-rose-900 via-red-800 to-rose-800', text: 'text-white', accent: 'text-rose-300' },
  { pattern: /GOLD|GOLDEN|HONEY|AMBER|BRONZE|COPPER|SAYLOR/i, bg: 'from-amber-800 via-yellow-700 to-amber-700', text: 'text-white', accent: 'text-yellow-200' },
  { pattern: /ORANGE|SUNSET|TANGERINE|PEACH/i, bg: 'from-orange-800 via-orange-700 to-amber-700', text: 'text-white', accent: 'text-orange-200' },
  { pattern: /PINK|LAVENDER|LILAC|ROSE|QUARTZ|MAUVE/i, bg: 'from-purple-900 via-pink-800 to-purple-800', text: 'text-white', accent: 'text-pink-300' },
  { pattern: /PURPLE|VIOLET|PLUM|AMETHYST/i, bg: 'from-purple-900 via-violet-800 to-purple-800', text: 'text-white', accent: 'text-violet-300' },
  { pattern: /GREY|GRAY|STORM|MIST|STEEL|SILVER|ENTRIA|MISHOL/i, bg: 'from-gray-800 via-slate-700 to-gray-700', text: 'text-white', accent: 'text-gray-300' },
  { pattern: /BROWN|WOOD|WALNUT|MAHOGANY|COCOA|COFFEE|ESPRESSO/i, bg: 'from-amber-950 via-amber-900 to-amber-800', text: 'text-white', accent: 'text-amber-300' },
  { pattern: /SAND|DESERT|BEIGE|TAN|CAMEL|DUNE/i, bg: 'from-amber-700 via-yellow-600 to-amber-600', text: 'text-white', accent: 'text-yellow-100' },
  { pattern: /MARBLE|CALACATTA|CARRARA|VEINED|STATUARIO/i, bg: 'from-gray-200 via-white to-gray-300', text: 'text-gray-900', accent: 'text-amber-700' },
  { pattern: /POOL|SWIM|WATER|RESORT|PIETRO|SONET/i, bg: 'from-cyan-800 via-blue-700 to-cyan-700', text: 'text-white', accent: 'text-cyan-200' },
  { pattern: /COCO|TROPICAL|PALM|ISLAND|PARADISE|HAVEN/i, bg: 'from-teal-800 via-emerald-700 to-teal-700', text: 'text-white', accent: 'text-teal-200' },
  { pattern: /FLUTE|LINEAR|WAVE|TEXTURED|GEOMETRIC/i, bg: 'from-stone-800 via-stone-700 to-stone-600', text: 'text-white', accent: 'text-stone-300' },
  { pattern: /SUBWAY|METRO|PENTE|CLASSIC/i, bg: 'from-gray-700 via-slate-600 to-gray-600', text: 'text-white', accent: 'text-gray-200' },
  { pattern: /SPARTEN|DOVER|CREMA|ENDLESS|OVIO/i, bg: 'from-stone-700 via-amber-800 to-stone-700', text: 'text-white', accent: 'text-amber-300' },
  { pattern: /DAZZY|JASPER|JIZZE|KOSHI/i, bg: 'from-gray-200 via-stone-100 to-gray-300', text: 'text-gray-900', accent: 'text-amber-600' },
  { pattern: /MASTER|ROYAL|IMPERIAL|HERITAGE|PALACE/i, bg: 'from-amber-900 via-yellow-800 to-amber-800', text: 'text-white', accent: 'text-yellow-300' },
];

const defaultTheme = { bg: 'from-stone-950 via-amber-950/90 to-stone-900', text: 'text-white', accent: 'text-amber-400' };

const getThemeForProduct = (productName) => {
  for (const theme of tileThemes) {
    if (theme.pattern.test(productName)) {
      return theme;
    }
  }
  return defaultTheme;
};

const COLS = 10;
const ROWS = 8;
const TILE_COUNT = COLS * ROWS;

const tileData = [...Array(TILE_COUNT)].map((_, i) => ({
  delay: (Math.random() * 4).toFixed(2),
  duration: (2 + Math.random() * 3).toFixed(2),
  rotateX: (Math.random() * 8 - 4).toFixed(1),
  rotateY: (Math.random() * 8 - 4).toFixed(1),
  z: Math.floor(Math.random() * 30),
  glowHue: Math.random() > 0.7 ? Math.floor(Math.random() * 60 + 20) : 0,
}));

const AnimatedTileGrid = React.memo(() => {
  const [activeTiles, setActiveTiles] = useState(new Set());
  const [glowTiles, setGlowTiles] = useState(new Set());

  useEffect(() => {
    const interval = setInterval(() => {
      const active = new Set();
      for (let i = 0; i < 15; i++) {
        active.add(Math.floor(Math.random() * TILE_COUNT));
      }
      setActiveTiles(active);

      const glow = new Set();
      for (let i = 0; i < 5; i++) {
        glow.add(Math.floor(Math.random() * TILE_COUNT));
      }
      setGlowTiles(glow);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ perspective: '800px', perspectiveOrigin: '50% 40%' }}>
      <div
        className="absolute grid"
        style={{
          inset: '-10%',
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          gridTemplateRows: `repeat(${ROWS}, 1fr)`,
          transform: 'rotateX(35deg) rotateZ(-5deg) scale(1.3)',
          transformStyle: 'preserve-3d',
        }}
      >
        {tileData.map((tile, i) => {
          const isActive = activeTiles.has(i);
          const isGlow = glowTiles.has(i);
          return (
            <div
              key={i}
              className="relative"
              style={{
                transformStyle: 'preserve-3d',
                animation: `tileFloat ${tile.duration}s ease-in-out ${tile.delay}s infinite`,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: '2px',
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: isActive
                    ? 'linear-gradient(135deg, rgba(245,158,11,0.25), rgba(249,115,22,0.15))'
                    : isGlow
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04))'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                  transform: `translateZ(${isActive ? tile.z + 15 : tile.z}px) rotateX(${tile.rotateX}deg) rotateY(${tile.rotateY}deg)`,
                  transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isActive
                    ? '0 0 20px rgba(245,158,11,0.3), inset 0 0 15px rgba(245,158,11,0.1)'
                    : isGlow
                    ? '0 0 12px rgba(255,255,255,0.08), inset 0 0 8px rgba(255,255,255,0.04)'
                    : '0 2px 8px rgba(0,0,0,0.2)',
                  backdropFilter: isActive ? 'blur(2px)' : 'none',
                }}
              >
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '4px',
                    background: 'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)',
                    animation: 'tileShine 2s ease-in-out infinite',
                  }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(ellipse at 50% 60%, transparent 30%, rgba(0,0,0,0.4) 100%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
});

const FloatingTile = React.memo(({ delay, size, position, rotation }) => (
  <div 
    className="absolute animate-float"
    style={{
      ...position,
      animationDelay: `${delay}s`,
      transform: `rotate(${rotation}deg)`,
    }}
  >
    <div 
      className={`${size} bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-lg border border-white/20`}
    />
  </div>
));

const buildRoundRobinProducts = () => {
  const catOrder = ['bank-series', 'coco-series', 'flute-series', 'golden-glossy', 'pool-mosaics', 'subway', 'swimming-pool'];
  const grouped = {};
  catOrder.forEach(cat => { grouped[cat] = []; });
  heroProducts.forEach(p => {
    if (grouped[p.category]) grouped[p.category].push(p);
  });
  const result = [];
  const maxLen = Math.max(...catOrder.map(c => grouped[c].length));
  for (let i = 0; i < maxLen; i++) {
    for (const cat of catOrder) {
      if (i < grouped[cat].length) {
        result.push(grouped[cat][i]);
      }
    }
  }
  return result;
};

const roundRobinProducts = buildRoundRobinProducts();

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const rafRef = useRef(null);
  
  const featuredProducts = roundRobinProducts;

  const currentProduct = featuredProducts[currentIndex % featuredProducts.length];
  const currentTheme = useMemo(() => getThemeForProduct(currentProduct?.name || ''), [currentProduct]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % featuredProducts.length);
        setIsTransitioning(false);
      }, 500);
    }, 6000);
    return () => clearInterval(interval);
  }, [featuredProducts.length]);

  const handleMouseMove = useCallback((e) => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
      rafRef.current = null;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleMouseMove]);

  const isLightTheme = currentTheme.text === 'text-gray-900';

  return (
    <section className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-all duration-1000 bg-gradient-to-br ${currentTheme.bg}`}>
      <AnimatedTileGrid />
      
      {currentProduct?.image && (
        <div 
          className={`absolute inset-0 transition-opacity duration-1000 ${isTransitioning ? 'opacity-0' : 'opacity-20'}`}
          style={{
            backgroundImage: `url(${getThumbPath(currentProduct.image)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(2px)',
          }}
        />
      )}
      
      <div className={`absolute inset-0 bg-gradient-to-b ${isLightTheme ? 'from-transparent via-white/30 to-white/60' : 'from-transparent via-black/30 to-black/60'}`} />

      <div className={`absolute top-1/4 right-1/4 w-[500px] h-[500px] ${isLightTheme ? 'bg-amber-500/10' : 'bg-white/10'} rounded-full blur-[100px] animate-pulse`} />
      <div className={`absolute bottom-1/3 left-1/4 w-[300px] h-[300px] ${isLightTheme ? 'bg-orange-500/10' : 'bg-white/5'} rounded-full blur-[80px] animate-pulse`} style={{ animationDelay: '1s' }} />

      <FloatingTile delay={0} size="w-16 h-16" position={{ top: '15%', left: '10%' }} rotation={15} />
      <FloatingTile delay={0.5} size="w-12 h-12" position={{ top: '25%', right: '15%' }} rotation={-10} />
      <FloatingTile delay={1} size="w-20 h-20" position={{ bottom: '20%', left: '5%' }} rotation={25} />
      <FloatingTile delay={1.5} size="w-10 h-10" position={{ bottom: '30%', right: '10%' }} rotation={-20} />

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="text-center lg:text-left order-2 lg:order-1">
            <div className={`inline-flex items-center gap-2 ${isLightTheme ? 'bg-amber-500/20 border-amber-600/30 text-amber-800' : 'bg-white/10 border-white/20 text-white'} backdrop-blur border px-5 py-2.5 rounded-full text-sm font-medium mb-8 animate-fade-in`}>
              <Sparkles className="w-4 h-4 animate-pulse" />
              Premium Australian Tile Distributor
              <ChevronRight className="w-4 h-4" />
            </div>
            
            <h1 className={`text-5xl sm:text-6xl lg:text-7xl font-bold ${currentTheme.text} mb-8 tracking-tight leading-[1.1] transition-colors duration-500`}>
              <span className="block">Transform Your</span>
              <span className={`block ${currentTheme.accent} animate-gradient`}>
                Space With
              </span>
              <span className="block">Premium Tiles</span>
            </h1>
            
            <p className={`text-xl ${isLightTheme ? 'text-gray-600' : 'text-white/70'} mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0 transition-colors duration-500`}>
              Discover {heroProducts.length}+ handpicked premium designs from Shreeji Global Organisation. 
              Elevating Australian homes with quality, elegance, and innovation.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a 
                href="#products" 
                className="group w-full sm:w-auto relative overflow-hidden bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-amber-500/25 flex items-center justify-center gap-3"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                <Grid3X3 className="w-5 h-5" />
                <span>Explore Collections</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a 
                href="#contact" 
                className={`group w-full sm:w-auto border-2 ${isLightTheme ? 'border-gray-400 text-gray-700 hover:border-amber-500' : 'border-white/30 text-white hover:border-white/50'} px-8 py-4 rounded-2xl font-bold hover:bg-white/5 transition-all flex items-center justify-center gap-2`}
              >
                <Play className="w-5 h-5" />
                Contact Us
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md mx-auto lg:mx-0">
              <div className="text-center lg:text-left">
                <p className={`text-4xl font-bold ${currentTheme.text} flex items-baseline gap-1 transition-colors duration-500`}>
                  {heroProducts.length}
                  <span className={`${currentTheme.accent} text-xl`}>+</span>
                </p>
                <p className={`text-sm ${isLightTheme ? 'text-gray-500' : 'text-white/50'} mt-1`}>Premium Tiles</p>
              </div>
              <div className="text-center lg:text-left">
                <p className={`text-4xl font-bold ${currentTheme.text} transition-colors duration-500`}>10</p>
                <p className={`text-sm ${isLightTheme ? 'text-gray-500' : 'text-white/50'} mt-1`}>Collections</p>
              </div>
              <div className="text-center lg:text-left">
                <p className={`text-4xl font-bold ${currentTheme.text} flex items-center gap-1 transition-colors duration-500`}>
                  5.0
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                </p>
                <p className={`text-sm ${isLightTheme ? 'text-gray-500' : 'text-white/50'} mt-1`}>Rating</p>
              </div>
            </div>
          </div>

          <div className="relative order-1 lg:order-2">
            <div 
              className="relative w-full max-w-lg mx-auto"
              style={{
                transform: `perspective(1000px) rotateY(${mousePos.x * 0.1}deg) rotateX(${-mousePos.y * 0.1}deg)`,
                transition: 'transform 0.15s ease-out',
              }}
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/30 via-orange-500/30 to-amber-500/30 rounded-[2rem] blur-2xl animate-pulse" />
              
              <div className={`relative aspect-square rounded-[2rem] overflow-hidden border-4 ${isLightTheme ? 'border-amber-400/60' : 'border-white/30'} shadow-2xl bg-gradient-to-br from-stone-800 to-stone-900 transition-all duration-500`}>
                {currentProduct?.image ? (
                  <img 
                    key={currentProduct.id}
                    src={getThumbPath(currentProduct.image)} 
                    alt={currentProduct.name}
                    className={`w-full h-full object-cover transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
                    onError={(e) => {
                      e.target.src = '/images/logo.jpg';
                    }}
                  />
                ) : (
                  <img 
                    src="/images/logo.jpg" 
                    alt="Shreeji Global Organisation"
                    className="w-full h-full object-contain p-8"
                  />
                )}
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="bg-black/70 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentTheme.bg} shadow-lg border border-white/20 overflow-hidden`}>
                        {currentProduct?.image && (
                          <img src={getThumbPath(currentProduct.image)} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-amber-400 text-sm font-medium">Featured Tile</p>
                        <p className={`text-white font-bold text-lg truncate transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                          {currentProduct?.name || 'Loading...'}
                        </p>
                        <p className="text-white/60 text-xs">{currentProduct?.categoryName} Collection</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex-1 bg-white/10 rounded-full h-1">
                        <div 
                          className="bg-amber-500 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${((currentIndex % featuredProducts.length + 1) / featuredProducts.length) * 100}%` }}
                        />
                      </div>
                      <span className="text-white/50 text-xs font-mono">
                        {currentIndex % featuredProducts.length + 1}/{featuredProducts.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>


              <div className={`absolute -bottom-4 -left-4 ${isLightTheme ? 'bg-white border-gray-200' : 'bg-black/80 border-white/20'} p-4 rounded-2xl shadow-xl border transform -rotate-3 hover:rotate-0 transition-transform`}>
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <span className={`${isLightTheme ? 'text-gray-900' : 'text-white'} font-bold text-sm`}>5.0</span>
                </div>
                <p className={`${isLightTheme ? 'text-gray-500' : 'text-white/60'} text-xs mt-1`}>Customer Rating</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-stone-100 via-stone-100/50 to-transparent" />
      
    </section>
  );
};

export default React.memo(Hero);
